import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft,
  Robot,
  FloppyDisk,
  ArrowCounterClockwise,
  Plus,
  X,
  ChatCircleText,
  Car,
  PhoneSlash,
  ShieldWarning,
  CheckSquare,
  Clock,
  Lightning,
  CircleNotch,
} from "@phosphor-icons/react";

// Section + key metadata. Keys marked with `bot` indicate which bot it
// affects (UI tabs filter by this).
const KEYWORD_FIELDS = [
  {
    key: "greeting_keywords",
    label: "Greeting Keywords",
    desc: "Customer types these → bot fires the cab-type menu",
    icon: ChatCircleText,
    bot: "rodbez",
  },
  {
    key: "booking_intent_phrases",
    label: "Booking Intent Phrases",
    desc: "Customer types these → bot fires booking flow without 'Hi'",
    icon: Car,
    bot: "rodbez",
  },
  {
    key: "ride_status_phrases",
    label: "Ride Status Phrases",
    desc: "Customer types these → bot fetches & shows status card (bypasses pause)",
    icon: Clock,
    bot: "rodbez",
  },
  {
    key: "resolution_button_phrases",
    label: "Resolution Phrases (whitelist)",
    desc: "These taps NEVER raise a complaint banner",
    icon: CheckSquare,
    bot: "shared",
  },
  {
    key: "complaint_button_keywords",
    label: "Complaint Button Keywords",
    desc: "Template button taps containing these → red complaint banner",
    icon: ShieldWarning,
    bot: "shared",
  },
  {
    key: "complaint_text_keywords",
    label: "Complaint Text Keywords",
    desc: "Free-text messages containing these → red complaint banner",
    icon: ShieldWarning,
    bot: "shared",
  },
];

const TIMING_FIELDS = [
  {
    key: "rodbez_pause_minutes",
    label: "RodBez bot pause (after agent reply)",
    unit: "min",
    min: 1, max: 1440,
    desc: "Agent reply karne par bot itne minute silent rahega.",
    bot: "rodbez",
  },
  {
    key: "fresh_conversation_idle_hours",
    label: "Fresh-conversation idle threshold",
    unit: "hour",
    min: 1, max: 168,
    desc: "Customer ka rodbez session itne ghante idle ho to next message pe bot menu fire karega.",
    bot: "rodbez",
  },
  {
    key: "session_active_hours",
    label: "Session active window",
    unit: "hour",
    min: 1, max: 24,
    desc: "Rodbez booking session itne ghante tak active mana jata hai.",
    bot: "rodbez",
  },
  {
    key: "missed_call_window_hours",
    label: "Missed-call template button window",
    unit: "hour",
    min: 1, max: 720,
    desc: "Missed-call template ke buttons itne ghante tak valid rahenge.",
    bot: "missed_call",
  },
  {
    key: "tata_followup_cooldown_minutes",
    label: "Tata follow-up cooldown",
    unit: "min",
    min: 1, max: 1440,
    desc: "Same caller pe itne minute ke andar repeat missed-call hone par template re-send nahi hoga.",
    bot: "missed_call",
  },
];

const TOGGLE_FIELDS = [
  {
    key: "enable_fresh_conversation_greet",
    label: "Fresh-conversation auto-greet",
    desc: "Naya customer / 1h+ idle customer ka koi bhi non-status message → menu auto-fire",
    bot: "rodbez",
  },
  {
    key: "enable_booking_intent_detection",
    label: "Booking intent detection",
    desc: "'Requesting cab from X to Y' jaise free-text par menu fire",
    bot: "rodbez",
  },
  {
    key: "enable_status_query_bypass",
    label: "Ride-status bypass pause",
    desc: "Ride ID query active pause me bhi reply paaye",
    bot: "rodbez",
  },
  {
    key: "enable_greeting_pause_bypass",
    label: "Greeting bypasses pause",
    desc: "Customer 'Hi' type kare to active pause bhi bypass ho",
    bot: "rodbez",
  },
];

const BOT_TABS = [
  { key: "rodbez", label: "RodBez Booking", color: "#10b981", icon: Car },
  { key: "missed_call", label: "Missed Call", color: "#f59e0b", icon: PhoneSlash },
  { key: "shared", label: "Shared / Complaints", color: "#6366f1", icon: ShieldWarning },
];

// ----- KeywordChips editor -----
function KeywordEditor({ field, value, onChange }) {
  const [draft, setDraft] = useState("");
  const list = Array.isArray(value) ? value : [];
  const Icon = field.icon || ChatCircleText;

  const add = () => {
    const v = draft.trim().toLowerCase();
    if (!v) return;
    if (list.includes(v)) {
      toast.error("Duplicate");
      return;
    }
    onChange([...list, v]);
    setDraft("");
  };

  const remove = (kw) => {
    onChange(list.filter((x) => x !== kw));
  };

  return (
    <div className="rounded-xl border border-zinc-700 bg-zinc-900/40 p-4 space-y-3" data-testid={`field-${field.key}`}>
      <div className="flex items-start gap-3">
        <div className="p-2 rounded-lg bg-indigo-500/15 border border-indigo-500/30 mt-0.5">
          <Icon size={16} className="text-indigo-300" weight="fill" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[14px] font-semibold text-white">{field.label}</div>
          <div className="text-[11px] text-zinc-400">{field.desc}</div>
          <div className="text-[10px] text-zinc-600 mt-0.5">
            <code>{field.key}</code> · {list.length} keyword{list.length === 1 ? "" : "s"}
          </div>
        </div>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {list.map((kw) => (
          <span key={kw} data-testid={`chip-${field.key}-${kw}`} className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] bg-zinc-800 text-zinc-200 border border-zinc-700">
            {kw}
            <button onClick={() => remove(kw)} className="hover:text-rose-400" data-testid={`chip-remove-${field.key}-${kw}`}>
              <X size={11} weight="bold" />
            </button>
          </span>
        ))}
        {list.length === 0 && (
          <span className="text-[11px] text-zinc-600 italic">No keywords yet</span>
        )}
      </div>
      <div className="flex gap-2">
        <input
          data-testid={`input-${field.key}`}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          placeholder="Add a keyword and press Enter"
          className="flex-1 bg-zinc-950 border border-zinc-700 rounded-lg px-3 py-1.5 text-sm text-zinc-200 placeholder-zinc-600 focus:outline-none focus:border-indigo-500"
        />
        <button
          data-testid={`add-${field.key}`}
          onClick={add}
          className="px-3 py-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white text-sm flex items-center gap-1"
        >
          <Plus size={13} weight="bold" /> Add
        </button>
      </div>
    </div>
  );
}

function NumberField({ field, value, onChange }) {
  return (
    <div className="rounded-xl border border-zinc-700 bg-zinc-900/40 p-4 flex items-start gap-3" data-testid={`field-${field.key}`}>
      <div className="p-2 rounded-lg bg-amber-500/15 border border-amber-500/30 mt-0.5">
        <Clock size={16} className="text-amber-300" weight="fill" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[14px] font-semibold text-white">{field.label}</div>
        <div className="text-[11px] text-zinc-400 mb-2">{field.desc}</div>
        <div className="flex items-center gap-2">
          <input
            data-testid={`input-${field.key}`}
            type="number"
            min={field.min}
            max={field.max}
            value={value ?? ""}
            onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
            className="bg-zinc-950 border border-zinc-700 rounded-lg px-3 py-1.5 text-sm text-zinc-200 w-32 focus:outline-none focus:border-amber-500"
          />
          <span className="text-[11px] text-zinc-500">{field.unit}</span>
          <span className="text-[10px] text-zinc-600 ml-auto"><code>{field.key}</code></span>
        </div>
      </div>
    </div>
  );
}

function ToggleField({ field, value, onChange }) {
  return (
    <div className="rounded-xl border border-zinc-700 bg-zinc-900/40 p-4 flex items-center gap-3" data-testid={`field-${field.key}`}>
      <div className="p-2 rounded-lg bg-emerald-500/15 border border-emerald-500/30">
        <Lightning size={16} className="text-emerald-300" weight="fill" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[14px] font-semibold text-white">{field.label}</div>
        <div className="text-[11px] text-zinc-400">{field.desc}</div>
        <div className="text-[10px] text-zinc-600"><code>{field.key}</code></div>
      </div>
      <button
        data-testid={`toggle-${field.key}`}
        onClick={() => onChange(!value)}
        className={`relative w-11 h-6 rounded-full transition ${value ? "bg-emerald-500" : "bg-zinc-700"}`}
        role="switch"
        aria-checked={!!value}
      >
        <span
          className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${value ? "left-[22px]" : "left-0.5"}`}
        />
      </button>
    </div>
  );
}

function BotManagement() {
  const navigate = useNavigate();
  const [activeBot, setActiveBot] = useState("rodbez");
  const [config, setConfig] = useState(null);
  const [defaults, setDefaults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/bot-config");
      setConfig(r.data?.config || {});
      setDefaults(r.data?.defaults || {});
      setDirty(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load bot config");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const setField = (key, val) => {
    setConfig((prev) => ({ ...(prev || {}), [key]: val }));
    setDirty(true);
  };

  const save = async () => {
    if (!config || !dirty) return;
    setSaving(true);
    try {
      const r = await api.put("/admin/bot-config", config);
      setConfig(r.data?.config || config);
      setDirty(false);
      toast.success("Saved — config live in 30s (cache TTL)");
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d?.message || err.message || "Save failed"));
    } finally {
      setSaving(false);
    }
  };

  const resetDefaults = async () => {
    if (!window.confirm("Restore stock defaults? Your edits will be lost.")) return;
    setSaving(true);
    try {
      const r = await api.post("/admin/bot-config/reset");
      setConfig(r.data?.config || {});
      setDirty(false);
      toast.success("Reset to defaults");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reset failed");
    } finally {
      setSaving(false);
    }
  };

  // Filter fields by active bot
  const showKeywords = KEYWORD_FIELDS.filter((f) => f.bot === activeBot || (activeBot === "shared" && f.bot === "shared"));
  const showTimings = TIMING_FIELDS.filter((f) => f.bot === activeBot);
  const showToggles = TOGGLE_FIELDS.filter((f) => f.bot === activeBot);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0b0d18] text-zinc-200 flex items-center justify-center">
        <CircleNotch size={20} className="animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="bot-management-page" className="min-h-screen bg-[#0b0d18] text-zinc-200 p-6 font-sans">
      <div className="max-w-6xl mx-auto space-y-5">
        <div className="flex items-center justify-between gap-3">
          <button onClick={() => navigate("/")} className="flex items-center gap-2 text-sm text-zinc-400 hover:text-white" data-testid="back-btn">
            <ArrowLeft size={16} /> Back to inbox
          </button>
          <div className="flex items-center gap-2">
            <button
              onClick={resetDefaults}
              disabled={saving}
              data-testid="reset-defaults-btn"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-xs disabled:opacity-50"
            >
              <ArrowCounterClockwise size={13} /> Reset defaults
            </button>
            <button
              onClick={save}
              disabled={!dirty || saving}
              data-testid="save-btn"
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium ${
                dirty && !saving
                  ? "bg-emerald-600 hover:bg-emerald-500 text-white"
                  : "bg-zinc-800 text-zinc-500 cursor-not-allowed"
              }`}
            >
              {saving ? <CircleNotch size={13} className="animate-spin" /> : <FloppyDisk size={13} weight="fill" />}
              {dirty ? "Save changes" : "Saved"}
            </button>
          </div>
        </div>

        <div className="flex items-start gap-3">
          <div className="p-3 rounded-lg bg-indigo-500/15 border border-indigo-500/30">
            <Robot size={24} className="text-indigo-300" weight="fill" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">Bot Management</h1>
            <p className="text-[12px] text-zinc-400">
              Edit keywords, pause durations, and feature toggles for every bot — no redeploy needed (30s cache TTL).
            </p>
            {config?.updated_at && (
              <p className="text-[11px] text-zinc-600 mt-0.5">
                Last edit: {new Date(config.updated_at).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })}
              </p>
            )}
          </div>
        </div>

        {/* Bot tabs */}
        <div className="flex items-center gap-1 bg-zinc-900/50 border border-zinc-700 rounded-lg p-1 w-fit" data-testid="bot-tabs">
          {BOT_TABS.map((t) => {
            const Icon = t.icon;
            const isActive = activeBot === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setActiveBot(t.key)}
                data-testid={`bot-tab-${t.key}`}
                className={`px-3 py-1.5 rounded text-[12px] font-medium transition flex items-center gap-1.5 ${isActive ? "text-white" : "text-zinc-400 hover:text-white"}`}
                style={isActive ? { background: t.color } : {}}
              >
                <Icon size={13} weight="fill" /> {t.label}
              </button>
            );
          })}
        </div>

        {/* Toggles */}
        {showToggles.length > 0 && (
          <div className="space-y-2">
            <div className="text-[11px] uppercase tracking-wider text-zinc-400">Feature toggles</div>
            <div className="grid md:grid-cols-2 gap-3">
              {showToggles.map((f) => (
                <ToggleField key={f.key} field={f} value={config?.[f.key]} onChange={(v) => setField(f.key, v)} />
              ))}
            </div>
          </div>
        )}

        {/* Timings */}
        {showTimings.length > 0 && (
          <div className="space-y-2">
            <div className="text-[11px] uppercase tracking-wider text-zinc-400">Timing & thresholds</div>
            <div className="grid md:grid-cols-2 gap-3">
              {showTimings.map((f) => (
                <NumberField key={f.key} field={f} value={config?.[f.key]} onChange={(v) => setField(f.key, v)} />
              ))}
            </div>
          </div>
        )}

        {/* Keywords */}
        {showKeywords.length > 0 && (
          <div className="space-y-2">
            <div className="text-[11px] uppercase tracking-wider text-zinc-400">Keyword lists</div>
            <div className="grid lg:grid-cols-2 gap-3">
              {showKeywords.map((f) => (
                <KeywordEditor key={f.key} field={f} value={config?.[f.key]} onChange={(v) => setField(f.key, v)} />
              ))}
            </div>
          </div>
        )}

        {showKeywords.length === 0 && showTimings.length === 0 && showToggles.length === 0 && (
          <div className="rounded-xl border border-zinc-700 bg-zinc-900/40 p-12 text-center text-zinc-500">
            No editable settings for this bot yet.
          </div>
        )}
      </div>
    </div>
  );
}

export default BotManagement;
